import axios from "axios";
import Cookies from "js-cookie";

// hrSystemServer API base URL
const HR_API_BASE_URL = "https://api.oneplace.hr";

/**
 * Get authorization headers with bearer token
 */
const getAuthHeaders = () => {
  const token = Cookies.get("chatToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * CV API service for interacting with hrSystemServer CV endpoints
 */
const cvApi = {
  /**
   * Extract phone number from JWT token stored in cookie
   * @returns {string|null} Phone number or null if not found
   */
  getPhoneFromToken: () => {
    const token = Cookies.get("chatToken");
    if (!token) return null;

    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.phone || decoded.sub || decoded.phoneNumber || null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  },

  /**
   * Get CV by phone number
   * @param {string} phone - Phone number
   * @returns {Promise<Object>} CV response
   */
  getCvByPhone: async (phone) => {
    try {
      const response = await axios.get(`${HR_API_BASE_URL}/v1/cv/by-phone`, {
        params: { phone },
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching CV:", error);
      throw error;
    }
  },

  /**
   * Find or create CV for phone number
   * @param {string} phone - Phone number
   * @returns {Promise<Object>} CV response
   */
  findOrCreateCv: async (phone) => {
    try {
      const response = await axios.post(
        `${HR_API_BASE_URL}/v1/cv/find-or-create`,
        null,
        {
          params: { phone },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error finding/creating CV:", error);
      throw error;
    }
  },

  /**
   * Get CV by ID
   * @param {number} cvId - CV ID
   * @returns {Promise<Object>} CV response
   */
  getCvById: async (cvId) => {
    try {
      const response = await axios.get(`${HR_API_BASE_URL}/v1/cv/${cvId}`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching CV:", error);
      throw error;
    }
  },

  /**
   * Update CV
   * @param {number} cvId - CV ID
   * @param {Object} data - CV data to update
   * @returns {Promise<Object>} Updated CV response
   */
  updateCv: async (cvId, data) => {
    try {
      const response = await axios.put(`${HR_API_BASE_URL}/v1/cv/${cvId}`, data, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error updating CV:", error);
      throw error;
    }
  },

  // ==================== Education ====================

  /**
   * Add education to CV
   * @param {number} cvId - CV ID
   * @param {Object} education - Education data
   * @returns {Promise<Object>} Education response
   */
  addEducation: async (cvId, education) => {
    try {
      const response = await axios.post(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/education`,
        education,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error adding education:", error);
      throw error;
    }
  },

  /**
   * Update education entry
   * @param {number} cvId - CV ID
   * @param {number} educationId - Education ID
   * @param {Object} education - Education data
   * @returns {Promise<Object>} Education response
   */
  updateEducation: async (cvId, educationId, education) => {
    try {
      const response = await axios.put(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/education/${educationId}`,
        education,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating education:", error);
      throw error;
    }
  },

  /**
   * Delete education entry
   * @param {number} cvId - CV ID
   * @param {number} educationId - Education ID
   * @returns {Promise<Object>} Response
   */
  deleteEducation: async (cvId, educationId) => {
    try {
      const response = await axios.delete(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/education/${educationId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting education:", error);
      throw error;
    }
  },

  // ==================== Experience ====================

  /**
   * Add work experience to CV
   * @param {number} cvId - CV ID
   * @param {Object} experience - Experience data
   * @returns {Promise<Object>} Experience response
   */
  addExperience: async (cvId, experience) => {
    try {
      const response = await axios.post(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/experience`,
        experience,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error adding experience:", error);
      throw error;
    }
  },

  /**
   * Update work experience entry
   * @param {number} cvId - CV ID
   * @param {number} experienceId - Experience ID
   * @param {Object} experience - Experience data
   * @returns {Promise<Object>} Experience response
   */
  updateExperience: async (cvId, experienceId, experience) => {
    try {
      const response = await axios.put(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/experience/${experienceId}`,
        experience,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating experience:", error);
      throw error;
    }
  },

  /**
   * Delete work experience entry
   * @param {number} cvId - CV ID
   * @param {number} experienceId - Experience ID
   * @returns {Promise<Object>} Response
   */
  deleteExperience: async (cvId, experienceId) => {
    try {
      const response = await axios.delete(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/experience/${experienceId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting experience:", error);
      throw error;
    }
  },

  // ==================== Skills ====================

  /**
   * Add skill to CV
   * @param {number} cvId - CV ID
   * @param {Object} skill - Skill data
   * @returns {Promise<Object>} Skill response
   */
  addSkill: async (cvId, skill) => {
    try {
      const response = await axios.post(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/skill`,
        skill,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error adding skill:", error);
      throw error;
    }
  },

  /**
   * Update skill entry
   * @param {number} cvId - CV ID
   * @param {number} skillId - Skill ID
   * @param {Object} skill - Skill data
   * @returns {Promise<Object>} Skill response
   */
  updateSkill: async (cvId, skillId, skill) => {
    try {
      const response = await axios.put(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/skill/${skillId}`,
        skill,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating skill:", error);
      throw error;
    }
  },

  /**
   * Delete skill entry
   * @param {number} cvId - CV ID
   * @param {number} skillId - Skill ID
   * @returns {Promise<Object>} Response
   */
  deleteSkill: async (cvId, skillId) => {
    try {
      const response = await axios.delete(
        `${HR_API_BASE_URL}/v1/cv/${cvId}/skill/${skillId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting skill:", error);
      throw error;
    }
  },
};

export default cvApi;
